import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Ear, FileText, Send } from 'lucide-react';

const steps = [
  {
    num: '01',
    icon: Ear,
    title: 'Listen',
    desc: 'VitalsFlow sits on the doctor\'s desk and listens to the patient visit. No buttons, no dictating.',
  },
  {
    num: '02',
    icon: FileText,
    title: 'Document',
    desc: 'It automatically writes the HIPAA-compliant EHR note and generates the correct ICD-10 and CPT billing codes.',
  },
  {
    num: '03',
    icon: Send,
    title: 'Submit',
    desc: 'The claim goes to insurance automatically — faster reimbursements, fewer denials, zero manual entry.',
  },
];

export default function HowItWorks() {
  const [ref, isVisible] = useScrollAnimation(0.1);

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-24 md:py-32 px-4 md:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div ref={ref} className={`scroll-fade-up ${isVisible ? 'visible' : ''}`}>
          <p className="uppercase text-xs tracking-[0.2em] text-vf-forest font-body font-semibold mb-4 text-center">
            How It Works
          </p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-vf-charcoal tracking-tight mb-4 text-center leading-tight">
            Three steps. Zero typing.
          </h2>
          <p className="font-body text-base md:text-lg text-vf-charcoal/60 text-center max-w-xl mx-auto mb-16">
            From patient visit to insurance claim — fully automated.
          </p>
        </div>

        <div className={`stagger-children ${isVisible ? 'visible' : ''} grid grid-cols-1 md:grid-cols-3 gap-0`}>
          {steps.map((step, i) => (
            <div
              key={step.num}
              data-testid={`step-${i + 1}`}
              className="relative p-8 md:p-10 border border-vf-sage bg-vf-salt group hover:bg-white transition-colors duration-300"
            >
              {/* Connector line on desktop */}
              {i < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-[1px] w-[2px] h-8 bg-vf-mint -translate-y-1/2 z-10" />
              )}
              <span className="font-heading font-bold text-5xl text-vf-sage group-hover:text-vf-mint transition-colors duration-300 leading-none">
                {step.num}
              </span>
              <div className="mt-6 mb-4">
                <step.icon size={28} strokeWidth={1.5} className="text-vf-forest" />
              </div>
              <h3 className="font-heading font-semibold text-2xl text-vf-charcoal mb-3">
                {step.title}
              </h3>
              <p className="font-body text-sm text-vf-charcoal/60 leading-relaxed">
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
